import { ChallengeDefinitionEntity, UserProfileEntity, UserChallengeEntity, ChallengeTeamEntity, ChallengeTeamJoinRequestEntity, EmailChallengeEntity } from '../../../meta-data/app-meta-data.service';

export type CentralMode = 'pre-streak' | 'during-streak' | 'after-streak' | 'unknown';
export interface Section<T> { loading: boolean; data: T; error: string; }
export interface ChallengeGoal {
    ChallengeGoalId: number; ChallengeDefinitionId: number; Title: string; Description?: string;
    MeasureKey: number; TargetValue: number; CurrencyCode?: string; ParentChallengeGoalId?: number;
    DisplayOrder: number; IsVisible: boolean; current: number | null;
}
export interface Recruiter { userId: number; name: string; accepted: number; rank: number; }
export function streakerYearLabel(startYear: number, seasonYear: number): string {
    if (!Number.isInteger(startYear) || startYear < 1900 || !Number.isInteger(seasonYear) || startYear > seasonYear) { return 'Not specified'; }
    const years = seasonYear - startYear + 1;
    const suffix = years % 100 >= 11 && years % 100 <= 13 ? 'th'
        : years % 10 === 1 ? 'st' : years % 10 === 2 ? 'nd' : years % 10 === 3 ? 'rd' : 'th';
    return `${years}${suffix} year · ${startYear}`;
}
export interface CentralState {
    loading: boolean; error: string; mode: CentralMode; readOnlyChallenge: boolean; challenge: ChallengeDefinitionEntity;
    userId: number; profile: UserProfileEntity; participant: UserChallengeEntity;
    membership: 'unknown' | 'unregistered' | 'registered'; daysUntilStart: number;
    teams: Section<ChallengeTeamEntity[]>; history: Section<EmailChallengeEntity[]>;
    requests: Section<ChallengeTeamJoinRequestEntity[]>;
    goals: Section<ChallengeGoal[]>; leaderboard: Section<Recruiter[]>;
    accepted: Section<number>; registrationCount: Section<number>;
    years: Section<{ label: string; count: number }[]>;
    emailLeaders: Section<{ name: string; sent: number; accepted: number }[]>;
}
export function section<T>(data: T, loading = false): Section<T> { return { data, loading, error: '' }; }
export function initialState(): CentralState {
    return { loading: true, error: '', mode: 'unknown', readOnlyChallenge: true, challenge: null, userId: 0,
        profile: null, participant: null, membership: 'unknown', daysUntilStart: 0,
        teams: section([]), requests: section([]), history: section([]), goals: section([]), leaderboard: section([]),
        accepted: section(0), registrationCount: section(0), years: section([]), emailLeaders: section([]) };
}
export function lifecycle(challenge: ChallengeDefinitionEntity, now = Date.now()): { mode: CentralMode; daysUntilStart: number; readOnlyChallenge: boolean } {
    const start = Date.parse(challenge.ChallengeStartDate), end = Date.parse(challenge.ChallengeEndDate);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) { return { mode: 'unknown', daysUntilStart: 0, readOnlyChallenge: true }; }
    return { mode: now < start ? 'pre-streak' : now < end ? 'during-streak' : 'after-streak',
        daysUntilStart: Math.max(0, Math.ceil((start - now) / 86400000)), readOnlyChallenge: challenge.IsCurrent !== true || now < start || now >= end };
}
export function goalPercent(goal: ChallengeGoal): number {
    return goal.current === null || !Number.isFinite(goal.current) || goal.TargetValue <= 0 ? 0
        : Math.max(0, Math.min(100, goal.current / goal.TargetValue * 100));
}
/** Phase 1 registration milestones; no ChallengeGoal table dependency. */
export function registrationGoals(challengeId: number, current: number | null): ChallengeGoal[] {
    return [750, 1000, 1250, 1500].map((target, index) => ({
        ChallengeGoalId: index + 1, ChallengeDefinitionId: challengeId,
        Title: index === 0 ? 'Streakers signed up' : 'Next registration milestone',
        MeasureKey: 1, TargetValue: target, ParentChallengeGoalId: index === 0 ? null : 1,
        DisplayOrder: index, IsVisible: true, current,
    }));
}
/** One current milestone per main goal. At the final target, retain its reached card. */
export function visibleMilestones(goals: ChallengeGoal[]): ChallengeGoal[] {
    return goals.filter(goal => !goal.ParentChallengeGoalId).sort((a, b) => a.DisplayOrder - b.DisplayOrder).map(root => {
        const milestones = [root, ...goals.filter(goal => goal.ParentChallengeGoalId === root.ChallengeGoalId)]
            .sort((a, b) => a.TargetValue - b.TargetValue);
        return milestones.find(goal => goal.current === null || goal.current < goal.TargetValue) || milestones[milestones.length - 1];
    });
}
export function validateGoals(goals: ChallengeGoal[]): ChallengeGoal[] {
    for (const goal of goals) {
        if (!Number.isFinite(Number(goal.TargetValue)) || Number(goal.TargetValue) <= 0 ||
            ![1, 2].includes(goal.MeasureKey) || !goal.Title ||
            (goal.MeasureKey === 1 && (!Number.isInteger(Number(goal.TargetValue)) || !!goal.CurrencyCode)) ||
            (goal.MeasureKey === 2 && !/^[A-Z]{3}$/.test(goal.CurrencyCode || ''))) { throw new Error('Invalid goal configuration.'); }
        if (goal.ParentChallengeGoalId) {
            const parent = goals.find(item => item.ChallengeGoalId === goal.ParentChallengeGoalId);
            if (!parent || parent.ParentChallengeGoalId || parent.ChallengeDefinitionId !== goal.ChallengeDefinitionId ||
                parent.MeasureKey !== goal.MeasureKey || parent.CurrencyCode !== goal.CurrencyCode ||
                Number(goal.TargetValue) <= Number(parent.TargetValue)) { throw new Error('Invalid stretch goal configuration.'); }
        }
    }
    return goals;
}
